import React, { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { User } from 'lucide-react';

interface PicDisplayProps {
  picIds: string[] | null;
  limit?: number;
}

interface PicProfile {
  id: string;
  name: string;
  email: string | null;
  profile_photo: string | null;
}

const PicDisplay: React.FC<PicDisplayProps> = ({ picIds, limit = 3 }) => {
  const [profiles, setProfiles] = useState<PicProfile[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchPicProfiles = async () => {
      if (!picIds || picIds.length === 0) return;
      
      setIsLoading(true);
      try {
        // Query for PIC profiles that match these names/IDs
        const { data, error } = await supabase
          .from('kol_pic')
          .select('id, name, email, profile_photo')
          .in('name', picIds)
          .order('created_at', { ascending: false })
          .limit(limit);
          
        if (error) throw error;
        
        setProfiles(data || []);
      } catch (err) {
        console.error('Error fetching PIC profiles:', err);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchPicProfiles();
  }, [picIds, limit]);
  
  const getInitials = (name: string | null): string => {
    if (!name) return '?';
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .substring(0, 2)
      .toUpperCase();
  };
  
  if (!picIds || picIds.length === 0) {
    return <span className="text-muted-foreground text-sm">No PIC</span>;
  }
  
  // Display loading state
  if (isLoading) {
    return (
      <div className="flex items-center gap-2">
        <Avatar className="h-6 w-6 border-2 border-background animate-pulse">
          <AvatarFallback className="bg-muted text-[10px]">...</AvatarFallback>
        </Avatar>
        <span className="text-sm text-muted-foreground">Loading...</span>
      </div>
    );
  }

  // If we found profiles, display them
  if (profiles.length > 0) {
    return (
      <TooltipProvider>
        <div className="flex items-center gap-2">
          <div className="flex -space-x-2">
            {profiles.slice(0, limit).map((profile) => (
              <Tooltip key={profile.id}>
                <TooltipTrigger asChild>
                  <Avatar className="h-6 w-6 border-2 border-background">
                    {profile.profile_photo ? (
                      <AvatarImage src={profile.profile_photo} alt={profile.name} />
                    ) : (
                      <AvatarFallback className="text-[10px] bg-primary text-primary-foreground">
                        {getInitials(profile.name)}
                      </AvatarFallback>
                    )}
                  </Avatar>
                </TooltipTrigger>
                <TooltipContent>
                  <div className="text-sm">
                    <p className="font-medium">{profile.name}</p>
                    {profile.email && (
                      <p className="text-xs text-muted-foreground">{profile.email}</p>
                    )}
                  </div>
                </TooltipContent>
              </Tooltip>
            ))}
            
            {/* If we have more PICs than the limit, show a +X more avatar */}
            {picIds.length > limit && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Avatar className="h-6 w-6 border-2 border-background bg-primary">
                    <AvatarFallback className="text-[10px] text-primary-foreground">
                      +{picIds.length - limit}
                    </AvatarFallback>
                  </Avatar>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{picIds.length - limit} more PICs</p>
                </TooltipContent>
              </Tooltip>
            )}
          </div>
          
          {/* Show first profile name */}
          <div className="text-sm min-w-[120px]">
            <p className="font-medium truncate max-w-[120px]">{profiles[0].name}</p>
            {profiles[0].email && (
              <p className="text-xs text-muted-foreground truncate max-w-[120px]">{profiles[0].email}</p>
            )}
            {profiles.length > 1 && (
              <p className="text-xs text-muted-foreground">+{profiles.length - 1} more</p>
            )}
          </div>
        </div>
      </TooltipProvider>
    );
  }

  // If no profiles were found but picIds exist, show the names directly
  return (
    <div className="flex items-center gap-2">
      <Avatar className="h-6 w-6 border-2 border-background">
        <AvatarFallback className="text-[10px] bg-muted"><User size={12} /></AvatarFallback>
      </Avatar>
      <div className="text-sm min-w-[120px]">
        <p className="font-medium truncate max-w-[120px]">{picIds[0]}</p>
        {picIds.length > 1 && (
          <p className="text-xs text-muted-foreground">+{picIds.length - 1} more</p>
        )}
      </div>
    </div>
  );
};

export default PicDisplay;